/**
 * ui.ts — the controls and chrome. Knows the DOM; knows nothing about the
 * engine, audio, or network. It calls back into the app through `UIHandlers`
 * and exposes setters the app uses to push state into the readout.
 *
 * Controls: a location search box, a "use my location" button, play/stop,
 * master volume, and a manual mode override. Plus the audio-unlock start overlay.
 */
import { CONTACT_PRESETS, contactMacroParams } from '@windchime/shared';
import type { ContactMacros, GeocodeResult } from '@windchime/shared';
import type { WeatherStatus } from './weather';
import { autoUnit, setUnitOverride, unitOverride } from './units';

export interface UIHandlers {
  onBegin(): void;
  onPlayToggle(): void;
  onSearch(q: string): void;
  /** fired (debounced) as the user types — drives the type-ahead dropdown */
  onQueryChange(q: string): void;
  /** clear the selected location and fall back to simulated wind */
  onClearLocation(): void;
  onKey(rootPc: number): void;
  onMode(name: string | null): void;
  onRingToggle(show: boolean): void;
  /** toggle the physical 'contact' strike model (vs statistical 'poisson') */
  onStrikeModel(contact: boolean): void;
  /** live-tweak contact-model params, e.g. { sailDrag: 1.2 } */
  onPhysics(patch: Record<string, number>): void;
  /** transpose the chime by whole octaves (clamped to the physical glockenspiel) */
  onOctave(delta: number): void;
}

/** One type-ahead row: a geocoded place, plus its distance from the user when known. */
export interface SearchItem {
  place: GeocodeResult;
  distanceKm?: number;
}

export interface ReadoutState {
  rootPc: number;
  modeName: string;
  windSpeed: number;
  windDirFrom: number;
  gust: number;
  tempF: number;
  localHour: number;
  activity: number;
}

// Display names for the 12 root pitch classes; flats suit the E♭ default house style.
const KEY_NAMES = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B'];

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const compass = (deg: number): string => COMPASS[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
const formatHour = (h: number): string => {
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = Math.floor(h) % 12 || 12;
  return `${h12} ${ap}`;
};
const round = (n: number): number => Math.round(n);
const formatKm = (km: number): string => (km < 1 ? '<1 km' : `${Math.round(km).toLocaleString()} km`);

function req<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} not found`);
  return el as T;
}

/** Place a popover toward whichever side of its anchor has room — the controls
 *  move around between layouts, so direction is measured, not assumed. Prefer
 *  below when it fits comfortably; otherwise take the roomier side. */
function orientPanel(anchor: HTMLElement, panel: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - 12;
  const above = r.top - 12;
  const down = below >= 260 || below >= above;
  panel.classList.toggle('down', down);
  panel.classList.toggle('up', !down);
  panel.style.maxHeight = `${Math.round(Math.max(120, Math.min(304, down ? below : above)))}px`;
}

interface DDOption {
  value: string;
  label: string;
  /** dimmer tagline, rendered like the type-ahead's region line */
  sub?: string;
}

// Mode list mirrors the engine's MODES names; '' = temperature-driven auto.
const MODE_OPTIONS: DDOption[] = [
  { value: '', label: 'Auto (temperature)' },
  { value: 'Lydian', label: 'Lydian', sub: 'brightest' },
  { value: 'Ionian', label: 'Ionian', sub: 'major' },
  { value: 'Mixolydian', label: 'Mixolydian' },
  { value: 'Dorian', label: 'Dorian' },
  { value: 'Aeolian', label: 'Aeolian', sub: 'minor' },
  { value: 'Phrygian', label: 'Phrygian' },
  { value: 'Locrian', label: 'Locrian', sub: 'darkest' },
];

/**
 * A custom select styled like the type-ahead dropdown (native <select> popups
 * can't be styled). Select-only combobox: focus stays on the trigger; ↑/↓ move
 * the highlight, Enter/Space pick, Escape/outside-click close.
 */
class Dropdown {
  private trigger: HTMLButtonElement;
  private panel: HTMLDivElement;
  private rows: HTMLDivElement[] = [];
  private activeIdx = -1;
  private isOpen = false;
  value: string;

  constructor(
    root: HTMLElement,
    private options: DDOption[],
    initial: string,
    private onChange: (value: string) => void,
  ) {
    this.value = initial;
    root.classList.add('dd');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'select dd-trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', `${root.id}-panel`);
    const rootLabel = root.getAttribute('aria-label');
    if (rootLabel) trigger.setAttribute('aria-label', rootLabel);
    trigger.innerHTML = '<span class="dd-label"></span><span class="dd-caret" aria-hidden="true">⌄</span>';
    this.trigger = trigger;

    const panel = document.createElement('div');
    panel.className = 'dd-panel';
    panel.id = `${root.id}-panel`;
    panel.setAttribute('role', 'listbox');
    panel.hidden = true;
    this.panel = panel;

    options.forEach((o, i) => {
      const row = document.createElement('div');
      row.className = 'item';
      row.id = `${root.id}-opt-${i}`;
      row.setAttribute('role', 'option');
      const place = document.createElement('span');
      place.className = 'place';
      const where = document.createElement('span');
      where.className = 'where';
      where.textContent = o.label;
      place.appendChild(where);
      if (o.sub) {
        const region = document.createElement('span');
        region.className = 'region';
        region.textContent = o.sub;
        place.appendChild(region);
      }
      row.appendChild(place);
      row.addEventListener('mouseenter', () => this.setActive(i));
      row.addEventListener('click', () => this.pick(i));
      this.rows.push(row);
      panel.appendChild(row);
    });

    root.append(trigger, panel);
    trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.openPanel()));
    trigger.addEventListener('keydown', (e) => this.onKey(e));
    document.addEventListener('click', (e) => {
      if (this.isOpen && !root.contains(e.target as Node)) this.close();
    });
    this.renderValue();
  }

  private renderValue(): void {
    const cur = this.options.find((o) => o.value === this.value);
    const label = this.trigger.querySelector<HTMLElement>('.dd-label');
    if (label) label.textContent = cur?.label ?? '';
    this.rows.forEach((r, i) => {
      const sel = this.options[i].value === this.value;
      r.classList.toggle('selected', sel);
      r.setAttribute('aria-selected', String(sel));
    });
  }

  /** Override the closed pill's displayed text independent of the selected value —
   *  lets the app show the engine's resolved name (e.g. the auto-driven mode). */
  setDisplay(text: string): void {
    const label = this.trigger.querySelector<HTMLElement>('.dd-label');
    if (label) label.textContent = text;
  }

  private openPanel(): void {
    this.isOpen = true;
    orientPanel(this.trigger, this.panel);
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.trigger.parentElement?.classList.add('open');
    const sel = this.options.findIndex((o) => o.value === this.value);
    this.setActive(sel >= 0 ? sel : 0);
  }

  private close(): void {
    this.isOpen = false;
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.removeAttribute('aria-activedescendant');
    this.trigger.parentElement?.classList.remove('open');
    this.rows[this.activeIdx]?.classList.remove('active');
    this.activeIdx = -1;
  }

  private setActive(i: number): void {
    this.rows[this.activeIdx]?.classList.remove('active');
    this.activeIdx = i;
    const row = this.rows[i];
    if (!row) return;
    row.classList.add('active');
    this.trigger.setAttribute('aria-activedescendant', row.id);
    row.scrollIntoView({ block: 'nearest' });
  }

  private pick(i: number): void {
    const o = this.options[i];
    if (!o) return;
    this.close();
    if (o.value !== this.value) {
      this.value = o.value;
      this.renderValue();
      this.onChange(o.value);
    }
  }

  private onKey(e: KeyboardEvent): void {
    const n = this.options.length;
    if (!this.isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openPanel();
      }
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const d = e.key === 'ArrowDown' ? 1 : -1;
      this.setActive(this.activeIdx < 0 ? (d > 0 ? 0 : n - 1) : (this.activeIdx + d + n) % n);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.pick(this.activeIdx);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this.setActive(n - 1);
    } else if (e.key === 'Tab') {
      this.close();
    }
  }
}

export class UI {
  private overlay = req<HTMLDivElement>('overlay');
  private startBtn = req<HTMLButtonElement>('startBtn');
  private rWind = req<HTMLDivElement>('r-wind');
  private locPill = req<HTMLDivElement>('loc-pill');
  private bClear = req<HTMLButtonElement>('b-clear');
  private form = req<HTMLFormElement>('loc-form');
  private q = req<HTMLInputElement>('q');
  private results = req<HTMLDivElement>('results');
  private sKey = req<HTMLDivElement>('s-key');
  private sMode = req<HTMLDivElement>('s-mode');
  private bPitchDown = req<HTMLButtonElement>('b-pdn');
  private bPitchUp = req<HTMLButtonElement>('b-pup');
  private keyDD!: Dropdown;
  private modeDD!: Dropdown;
  private bPlay = req<HTMLButtonElement>('b-play');
  private bArc = req<HTMLButtonElement>('b-arc');
  private bPhysics = req<HTMLButtonElement>('b-physics');
  private physPanel = req<HTMLDivElement>('phys');
  private presets = req<HTMLDivElement>('presets');
  private presetBtns: HTMLButtonElement[] = [];
  private hint = req<HTMLSpanElement>('hint');

  /** country of the selected live location — drives the auto wind unit */
  private locCountry: string | undefined;
  private lastReadout: ReadoutState | null = null;
  // type-ahead dropdown state
  private taTimer?: ReturnType<typeof setTimeout>;
  private items: SearchItem[] = [];
  private rows: HTMLDivElement[] = [];
  private activeIdx = -1;
  private onPick: ((r: GeocodeResult) => void) | null = null;
  // stable nodes for the wind line, so re-rendering every frame doesn't recreate the
  // hovered .unit element (which caused a hover flicker while playing)
  private wEls: {
    unit: HTMLElement;
    val: HTMLElement;
    uLabel: HTMLElement;
    dir: HTMLElement;
    gust: HTMLElement;
    temp: HTMLElement;
    hour: HTMLElement;
  } | null = null;

  constructor(h: UIHandlers) {
    this.startBtn.addEventListener('click', () => h.onBegin());

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (this.taTimer) clearTimeout(this.taTimer);
      // Enter with the dropdown open picks the highlighted (or first) match
      if (!this.results.hidden && this.items.length > 0) {
        this.pickItem(this.activeIdx >= 0 ? this.activeIdx : 0);
        return;
      }
      const q = this.q.value.trim();
      if (q) h.onSearch(q);
    });

    // debounced type-ahead while typing
    this.q.addEventListener('input', () => {
      if (this.taTimer) clearTimeout(this.taTimer);
      const q = this.q.value.trim();
      if (q.length < 2) {
        this.clearResults();
        return;
      }
      this.taTimer = setTimeout(() => h.onQueryChange(q), 250);
    });

    // arrow keys walk the dropdown visually; Escape closes it. When the dropdown
    // opens upward (column-reverse), item 0 — the nearest match — sits at the
    // BOTTOM next to the input, so the key pointing at the list flips: ArrowUp
    // enters at the nearest match and walks away from the input.
    this.q.addEventListener('keydown', (e) => {
      if (this.results.hidden || this.items.length === 0) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const reversed = getComputedStyle(this.results).flexDirection === 'column-reverse';
        const intoList = reversed ? 'ArrowUp' : 'ArrowDown'; // toward item 0 / the list
        const n = this.items.length;
        const next =
          this.activeIdx < 0
            ? e.key === intoList
              ? 0
              : n - 1
            : (this.activeIdx + (e.key === intoList ? 1 : -1) + n) % n;
        this.setActive(next);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.clearResults();
      }
    });

    // desktop nicety: the space bar toggles play/pause (unless typing or on a control)
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'Space') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON' || t.isContentEditable)) return;
      if (this.overlay.style.display !== 'none') return; // intro still up
      e.preventDefault();
      h.onPlayToggle();
    });

    this.bClear.addEventListener('click', () => {
      this.q.value = '';
      this.clearResults();
      h.onClearLocation();
    });

    this.keyDD = new Dropdown(this.sKey, KEY_NAMES.map((label, i) => ({ value: String(i), label })), '3', (v) => {
      h.onKey(Number(v));
    });

    this.modeDD = new Dropdown(this.sMode, MODE_OPTIONS, '', (v) => {
      h.onMode(v || null);
    });

    this.bPlay.addEventListener('click', () => h.onPlayToggle());

    this.bArc.addEventListener('click', () => {
      const on = !this.bArc.classList.contains('on');
      this.bArc.classList.toggle('on', on);
      h.onRingToggle(on);
    });

    this.bPhysics.addEventListener('click', () => {
      const on = !this.bPhysics.classList.contains('on');
      this.bPhysics.classList.toggle('on', on);
      this.physPanel.hidden = !on;
      h.onStrikeModel(on);
    });
    // The three macro sliders (wind · mass · size). Any change re-derives the full
    // engine param patch through the shared macro mapping.
    const macroEls = {
      wind: { el: req<HTMLInputElement>('ph-wind'), val: req<HTMLElement>('v-wind') },
      mass: { el: req<HTMLInputElement>('ph-mass'), val: req<HTMLElement>('v-mass') },
      size: { el: req<HTMLInputElement>('ph-size'), val: req<HTMLElement>('v-size') },
    };
    const readMacros = (): ContactMacros => ({
      wind: Number(macroEls.wind.el.value),
      mass: Number(macroEls.mass.el.value),
      size: Number(macroEls.size.el.value),
    });
    const syncMacroLabels = (): void => {
      for (const k of ['wind', 'mass', 'size'] as const) {
        macroEls[k].val.textContent = `${Math.round(Number(macroEls[k].el.value) * 100)}%`;
      }
    };
    syncMacroLabels();
    for (const k of ['wind', 'mass', 'size'] as const) {
      macroEls[k].el.addEventListener('input', () => {
        syncMacroLabels();
        this.clearPresetHighlight();
        h.onPhysics(contactMacroParams(readMacros()) as Record<string, number>);
      });
    }

    // Preset buttons: load a macro triple into the sliders and the engine in one go.
    // Hidden for now — flip to true to bring Calm/Breezy/Storm back.
    const SHOW_PRESETS = false;
    if (SHOW_PRESETS) CONTACT_PRESETS.forEach((preset) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = preset.label;
      btn.addEventListener('click', () => {
        macroEls.wind.el.value = String(preset.macros.wind);
        macroEls.mass.el.value = String(preset.macros.mass);
        macroEls.size.el.value = String(preset.macros.size);
        syncMacroLabels();
        this.presetBtns.forEach((b) => b.classList.toggle('on', b === btn));
        h.onPhysics(contactMacroParams(preset.macros) as Record<string, number>);
      });
      this.presetBtns.push(btn);
      this.presets.appendChild(btn);
    });

    // pitch ± steppers: octave transpose, clamped to the physical glockenspiel
    this.bPitchDown.addEventListener('click', () => h.onOctave(-1));
    this.bPitchUp.addEventListener('click', () => h.onOctave(1));

    // click (or Enter/Space) the wind speed to switch km/h <-> mph
    this.rWind.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.unit')) this.toggleWindUnit();
    });
    this.rWind.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && (e.target as HTMLElement).closest('.unit')) {
        e.preventDefault();
        this.toggleWindUnit();
      }
    });

    // close the results dropdown when clicking outside the location form
    document.addEventListener('click', (e) => {
      if (!this.form.contains(e.target as Node)) this.clearResults();
    });
  }

  /** A manual slider tweak means the live values no longer match any preset. */
  private clearPresetHighlight(): void {
    this.presetBtns.forEach((b) => b.classList.remove('on'));
  }

  /** Refresh the pitch stepper: the chime starts in its middle octave and can go
   *  exactly one octave up or down (no readout; the octave is heard, not displayed). */
  setPitchState(octaveOffset: number): void {
    this.bPitchDown.disabled = octaveOffset <= -1;
    this.bPitchUp.disabled = octaveOffset >= 1;
  }

  /** The unit to display right now: the user's clicked choice while its 20-minute
   *  window lasts, else auto from the selected country / browser locale. */
  private currentUnit(): 'kmh' | 'mph' {
    return unitOverride() ?? autoUnit(this.locCountry);
  }

  private toggleWindUnit(): void {
    setUnitOverride(this.currentUnit() === 'kmh' ? 'mph' : 'kmh'); // kept for 20 min, then auto again
    if (this.lastReadout) this.renderWind(this.lastReadout);
  }

  /** The single-line weather readout in the pill: wind · gust | temp · time.
   *  Builds stable nodes once, then only updates text (so the hovered .unit element
   *  is never recreated — no hover flicker while playing). */
  private renderWind(s: ReadoutState): void {
    if (!this.wEls) {
      // two atomic groups (wind, temp) so on narrow screens they wrap as units
      // inside the pill instead of one long line overflowing it
      this.rWind.innerHTML =
        '<span class="wgrp">wind <span class="unit" role="button" tabindex="0"><b class="wv"></b> <span class="wu"></span></span>' +
        ' <span class="wd"></span> · gust <b class="wg"></b></span>' +
        '<span class="wsep" aria-hidden="true"></span>' +
        '<span class="wgrp">temp <b class="wt"></b> · <span class="wh"></span></span>';
      this.wEls = {
        unit: this.rWind.querySelector('.unit') as HTMLElement,
        val: this.rWind.querySelector('.wv') as HTMLElement,
        uLabel: this.rWind.querySelector('.wu') as HTMLElement,
        dir: this.rWind.querySelector('.wd') as HTMLElement,
        gust: this.rWind.querySelector('.wg') as HTMLElement,
        temp: this.rWind.querySelector('.wt') as HTMLElement,
        hour: this.rWind.querySelector('.wh') as HTMLElement,
      };
    }
    const mph = this.currentUnit() === 'mph';
    this.wEls.val.textContent = String(round(mph ? s.windSpeed * 0.621371 : s.windSpeed));
    this.wEls.uLabel.textContent = mph ? 'mph' : 'km/h';
    this.wEls.unit.title = `Click to show ${mph ? 'km/h' : 'mph'} (kept for 20 min)`;
    this.wEls.dir.textContent = compass(s.windDirFrom);
    this.wEls.gust.textContent = `${s.gust.toFixed(2)}×`;
    this.wEls.temp.textContent = `${round(s.tempF)}°F`;
    this.wEls.hour.textContent = formatHour(s.localHour);
  }

  hideOverlay(): void {
    this.overlay.style.display = 'none';
  }

  /**
   * Intro fly-through: the title scales up and exits upward while the sub-heading
   * dives down — the camera passes between them. On arrival the header echoes it in
   * reverse (sub-heading then title dropping in from the top), and the rest of the
   * main screen reveals in a random staggered order as the overlay clears. Honors
   * prefers-reduced-motion by cutting straight to the revealed layout.
   */
  playIntro(): void {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const FLY_MS = reduced ? 300 : 1150; // matches the CSS animation length
    const REVEAL_AT = reduced ? 250 : 800; // start revealing before the overlay is fully gone
    const STEP_MIN = 70;
    const STEP_MAX = 160; // ms between staggered reveals

    this.overlay.classList.add('flythrough');
    // reveal the main screen while the overlay is still fading — it materializes as we arrive
    window.setTimeout(() => {
      // the header's own entrance: sub-heading drops in first, then the title (CSS delays)
      document.querySelector('.title')?.classList.add('in');
      // random staggered order: shuffle the reveal elements (Fisher–Yates), then give each a
      // cumulative random delay via a CSS custom property, so they cascade in one at a time.
      const els = Array.from(document.querySelectorAll<HTMLElement>('.reveal-item'));
      for (let i = els.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [els[i], els[j]] = [els[j], els[i]];
      }
      let t = 0;
      for (const el of els) {
        el.style.setProperty('--reveal-delay', `${Math.round(t)}ms`);
        t += STEP_MIN + Math.random() * (STEP_MAX - STEP_MIN);
      }
      void document.body.offsetWidth; // flush so the delays register before .in lands
      els.forEach((el) => el.classList.add('in'));
    }, REVEAL_AT);

    // drop the overlay from the tree once everything has finished
    window.setTimeout(() => {
      this.overlay.style.display = 'none';
    }, FLY_MS + 600);
  }

  focusSearch(): void {
    this.q.focus();
  }

  setPlaying(playing: boolean): void {
    this.bPlay.classList.toggle('on', playing);
    this.bPlay.textContent = playing ? 'Playing' : 'Paused';
  }


  setHint(text: string, warn = false): void {
    this.hint.textContent = text;
    this.hint.classList.toggle('warn', warn);
  }

  /** Render the type-ahead dropdown. Items arrive sorted (nearest first when known). */
  showResults(items: SearchItem[], onPick: (r: GeocodeResult) => void): void {
    this.items = items;
    this.rows = [];
    this.onPick = onPick;
    this.activeIdx = -1;
    this.results.innerHTML = '';
    if (items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.textContent = 'No matches — try a different spelling.';
      this.results.appendChild(empty);
    } else {
      items.forEach((it, i) => {
        const row = document.createElement('div');
        row.className = 'item';
        row.id = `opt-${i}`;
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', 'false');
        const place = document.createElement('span');
        place.className = 'place';
        const where = document.createElement('span');
        where.className = 'where';
        where.textContent = it.place.name;
        place.appendChild(where);
        const regionText = [it.place.admin1, it.place.country].filter(Boolean).join(', ');
        if (regionText) {
          const region = document.createElement('span');
          region.className = 'region';
          region.textContent = regionText;
          place.appendChild(region);
        }
        row.appendChild(place);
        if (it.distanceKm != null) {
          const dist = document.createElement('span');
          dist.className = 'dist';
          dist.textContent = formatKm(it.distanceKm);
          row.appendChild(dist);
        }
        row.addEventListener('mouseenter', () => this.setActive(i));
        row.addEventListener('click', () => this.pickItem(i));
        this.rows.push(row);
        this.results.appendChild(row);
      });
    }
    orientPanel(this.form, this.results);
    this.results.hidden = false;
    this.q.setAttribute('aria-expanded', 'true');
  }

  clearResults(): void {
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.items = [];
    this.rows = [];
    this.activeIdx = -1;
    this.q.setAttribute('aria-expanded', 'false');
    this.q.removeAttribute('aria-activedescendant');
  }

  private setActive(i: number): void {
    const prev = this.rows[this.activeIdx];
    if (prev) {
      prev.classList.remove('active');
      prev.setAttribute('aria-selected', 'false');
    }
    this.activeIdx = i;
    const row = this.rows[i];
    if (!row) return;
    row.classList.add('active');
    row.setAttribute('aria-selected', 'true');
    this.q.setAttribute('aria-activedescendant', row.id);
    row.scrollIntoView({ block: 'nearest' });
  }

  private pickItem(i: number): void {
    const it = this.items[i];
    if (!it) return;
    this.q.value = it.place.name;
    this.clearResults();
    this.onPick?.(it.place);
  }

  setReadout(s: ReadoutState): void {
    this.lastReadout = s;
    // the pills are the single, large display of the current key/mode (resolved — so the
    // mode pill shows the actual auto-driven mode, not just "Auto")
    this.keyDD.setDisplay(KEY_NAMES[s.rootPc]);
    this.modeDD.setDisplay(s.modeName);
    this.renderWind(s);
  }

  setSource(s: WeatherStatus): void {
    this.bClear.hidden = s.mode !== 'live'; // only offer "clear" once a place is chosen
    this.locCountry = s.mode === 'live' ? s.country : undefined;
    if (s.mode === 'live') {
      // the search input doubles as the location display: show the chosen place in it
      this.q.value = s.label ?? 'live';
      const src =
        s.source === 'nws'
          ? `NWS${s.station ? ` · ${s.station}` : ''}${s.distanceKm != null ? ` · ${round(s.distanceKm)} km` : ''}`
          : 'Open-Meteo · modeled';
      this.locPill.title = src; // station detail lives in the tooltip now
      this.setHint('', false);
    } else {
      if (document.activeElement !== this.q) this.q.value = ''; // placeholder carries the prompt
      this.locPill.title = 'Simulated wind';
      if (s.error) this.setHint(`Live weather failed: ${s.error} — simulating.`, true);
      else this.setHint(s.note ?? '', false);
    }
    if (this.lastReadout) this.renderWind(this.lastReadout); // unit may change with the country
  }
}
